import { JupiterTheme } from '@jupiterone/jupiter-web-toolkit';
import { Checkbox, FormControlLabel } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import { createStyles, makeStyles } from '@material-ui/styles';
import clsx from 'clsx';
import React from 'react';
import RuleOutputIcon from 'jupiter-web-alerts/src/components/Rules/RuleOutputIcon';
import RuleActionConfig from './actionConfigs/RuleActionConfig';
import BasicRuleFields from './BasicRuleFields';
import useBasicRuleEditor, {
  HookProps,
  ADDITIONAL_ACTIONS,
} from './useBasicRuleEditor';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    config: {
      display: 'flex',
    },
    chips: {
      display: 'flex',
      flexWrap: 'wrap',
    },
    chip: {
      margin: 2,
    },
    formControl: {
      margin: theme.spacing(1),
      minWidth: 120,
      maxWidth: 300,
    },
    subtitle: {
      display: 'block',
      color: theme.palette.primary.main,
      marginBottom: theme.spacing(2),
    },
    additionalRow: {
      borderBottom: `solid 2px ${theme.palette.common.white}`,
      maxWidth: '100%',
      display: 'grid',
      gridTemplateColumns: '1fr 2fr',
      padding: 0,
      '& > *': {
        background: 'rgba(239, 240, 245, 0.5)',
        height: '100%',
        margin: 0,
        padding: theme.spacing(2),
      },
      '& > label': {
        alignItems: 'flex-start',
        background: theme.palette.coolGrey[50],
        display: 'flex',
        marginRight: 0,
        minWidth: 260,
        paddingTop: 31,
      },
    },
    checkboxRow: {
      maxWidth: '100%',
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
    },
    checkbox: {
      marginLeft: 0,
      '&&': {
        alignItems: 'center',
      },
    },
    actionLabel: {
      display: 'flex',
      alignItems: 'center',
    },
  })
);

interface Props extends HookProps {
  readonly?: boolean;
  tags: string[] | undefined;
}

const BasicRuleEditor = ({ rule, onChange, readonly, tags }: Props) => {
  const classes = useStyles();
  const {
    selectedActions,
    handleActionChange,
    handleChange,
    handleUpdateAction,
    getInitialActionData,
  } = useBasicRuleEditor({ rule, onChange });

  return (
    <>
      <BasicRuleFields
        readonly={readonly}
        onChange={handleChange}
        rule={rule}
        tags={tags}
        onTagChange={onChange}
      />
      <Typography variant="subtitle2" className={classes.subtitle}>
        Additional Actions
      </Typography>
      {ADDITIONAL_ACTIONS.map((action) => (
        <div
          className={clsx(classes.checkboxRow, classes.additionalRow)}
          key={action}
        >
          <FormControlLabel
            control={
              <Checkbox
                disabled={readonly}
                checked={selectedActions.indexOf(action) >= 0}
                onChange={handleActionChange}
                value={action}
              />
            }
            className={classes.checkbox}
            label={
              <span className={classes.actionLabel}>
                <RuleOutputIcon noBorder={true} action={action} />
                {action}
              </span>
            }
          />
          <RuleActionConfig
            actionType={action}
            onUpdate={handleUpdateAction}
            disabled={selectedActions.indexOf(action) === -1 || readonly}
            initialActionData={getInitialActionData(action)}
            templateData={rule.templates}
          />
        </div>
      ))}
    </>
  );
};

export default BasicRuleEditor;
